import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, Upload } from "antd";

import { Icons } from "components/layout";
import { Loader } from "components/request-result";
import Localize from "components/service/localize";
import TranslatedTextareaControl from "components/service/translated-textarea-control";
import { TRANSLATION_FIELD_TYPES } from "mock-data/translation/languages";

import { AttachmentItem } from "./stand-comment-item";
import CommentsHelpers from "./helpers";

const StandCommentModal = ({
    state,
    onClose,
    onSubmit,
    onTextChange,
    onTranslationStateChange,
    onAttachmentUpload,
    onAttachmentDelete,
    isDraftCreating,
    updateLoading,
    loadingAttachmentCreate
}) => {
    const [ pendingFile, setPendingFile ] = useState(null);
    const attachmentsCountRef = useRef(0);

    useEffect(() => {
        if (!loadingAttachmentCreate) {
            setPendingFile(null);
        }
    }, [ loadingAttachmentCreate ]);

    useEffect(() => {
        if (!state.open) {
            setPendingFile(null);
        }
    }, [ state.open ]);

    const pendingParts = pendingFile
        ? CommentsHelpers.Mappers.splitFileName(pendingFile.name, "")
        : null;
    const resetKey = state.mode === "add" ?
        `add:${ state.sessionId || "closed" }:${ state.open ? "open" : "closed" }` :
        `edit:${ state.commentId || "closed" }:${ state.open ? "open" : "closed" }`;

    const handleBeforeUpload = (file) => {
        attachmentsCountRef.current = state.attachments.length;
        setPendingFile({ name: file.name });
        return onAttachmentUpload({ file });
    };

    return (
        <Modal
            placement="bottomCenter"
            className="modal-standard stand-comments-modal grey"
            wrapClassName="stand-comments-modal-wrap"
            visible={ state.open }
            onCancel={ onClose }
            footer={ null }
            centered={ true }
            title={
                state.mode === "add" ?
                    <Localize>STAND.Modal_Title_AddComment</Localize> :
                    <Localize>STAND.Modal_Title_EditComment</Localize>
            }
        >
            { state.loading ?
                <Loader type="block" /> :
                <div className="stand-comment-modal-content">
                    <TranslatedTextareaControl
                        rows={ 4 }
                        value={ state.text }
                        onChange={ (e) => onTextChange(e.target.value) }
                        initialTranslatedValue={ state.initialText }
                        namespace={ TRANSLATION_FIELD_TYPES.comment }
                        context={ `stand-comment:${ state.commentId || state.draftCommentId || state.sessionId || "new" }` }
                        resetKey={ resetKey }
                        onTranslationStateChange={ onTranslationStateChange }
                    />
                    { (!!state.attachments.length || pendingFile) &&
                        <ul className="attachment-list">
                            { state.attachments.map((attachment) => (
                                <AttachmentItem
                                    key={ attachment.id }
                                    attachment={ attachment }
                                    onDelete={ onAttachmentDelete }
                                />
                            )) }
                            { pendingFile && state.attachments.length === attachmentsCountRef.current && (
                                <li className="attachment-list-item--uploading">
                                    <div className="attachment-uploading-row">
                                        <div className="file">
                                            <Icons.Attachment />
                                            <span className="file-title">
                                                <span className="file-base">{ pendingParts.base }</span>
                                                <span className="file-ext">{ pendingParts.extension }</span>
                                            </span>
                                        </div>
                                        <span className="uploading-label">
                                            <Localize>COMMENTS.Text_Uploading</Localize>
                                        </span>
                                    </div>
                                    <div className="upload-progress-bar">
                                        <div className="upload-progress-fill" />
                                    </div>
                                </li>
                            ) }
                        </ul>
                    }
                    { state.attachments.length < 3 &&
                        (state.mode !== "add" || !!state.draftCommentId) &&
                        <Upload
                            showUploadList={ false }
                            beforeUpload={ handleBeforeUpload }
                        >
                            <button type="button" className="add-file-button">
                                <Icons.Cross className="icon-plus" />
                                <Localize>STAND.Text_AddFile</Localize>
                            </button>
                        </Upload>
                    }
                    <div className="actions">
                        <Button type="default" className="dark" onClick={ onClose }>
                            <Localize>GLOBAL.Button_Text_Cancel</Localize>
                        </Button>
                        <Button
                            type="primary"
                            onClick={ onSubmit }
                            loading={ updateLoading || isDraftCreating }
                            disabled={ isDraftCreating }
                        >
                            { state.mode === "add" ?
                                <Localize>STAND.Modal_Button_AddComment</Localize> :
                                <Localize>STAND.Modal_Button_SaveComment</Localize>
                            }
                        </Button>
                    </div>
                </div>
            }
        </Modal>
    );
};

export default StandCommentModal;
